import { Component } from '@theme/component';

/**
 * Product information tabs (APG tabs pattern).
 *
 * The block server-renders every panel visible and stacked, so the content is
 * readable without JavaScript. This component collapses them to a single
 * visible panel on connect and owns selection state from then on: roving
 * tabindex, `aria-selected`, and the `hidden` attribute on inactive panels.
 *
 * @typedef {object} ProductTabsRefs
 * @property {HTMLButtonElement[]} tabs - The tab buttons, in DOM order.
 * @property {HTMLElement[]} panels - The tab panels, in DOM order.
 *
 * @extends {Component<ProductTabsRefs>}
 */
export class ProductTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  /**
   * The `data-tab` value of the selected tab, or null before the first sync.
   *
   * @type {string | null}
   */
  #selected = null;

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  /**
   * Re-applies selection after a Section Rendering API re-render, which
   * restores the server-rendered state (first tab active, no panel hidden).
   */
  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  /**
   * Selects the previously selected tab when it still exists, otherwise the
   * first one.
   */
  #sync() {
    const { tabs } = this.refs;

    if (!tabs?.length) return;

    const stillPresent = tabs.some((tab) => tab.dataset.tab === this.#selected);

    this.#select(stillPresent ? this.#selected : (tabs[0]?.dataset.tab ?? null));
  }

  /**
   * Activates one tab and its panel, hiding the others.
   *
   * @param {string | null} name - The `data-tab` value to activate.
   * @param {object} [options]
   * @param {boolean} [options.focus=false] - Move focus to the activated tab.
   */
  #select(name, { focus = false } = {}) {
    if (!name) return;

    const { tabs, panels } = this.refs;

    this.#selected = name;

    for (const tab of tabs) {
      const active = tab.dataset.tab === name;

      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;

      if (active && focus) tab.focus();
    }

    for (const panel of panels) {
      panel.hidden = panel.dataset.tab !== name;
    }
  }

  /**
   * Activates the clicked tab.
   *
   * @param {MouseEvent} event - The click event, retargeted to the tab button.
   */
  selectTab = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target);

    this.#select(tab.dataset.tab ?? null);
  };

  /**
   * Roving-tabindex keyboard navigation: ArrowLeft/ArrowRight (direction-aware),
   * Home, and End move selection and focus together.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeyDown = (event) => {
    const { tabs } = this.refs;
    const last = tabs.length - 1;

    if (last < 0) return;

    const current = Math.max(
      0,
      tabs.findIndex((tab) => tab.dataset.tab === this.#selected)
    );
    const forward = getComputedStyle(this).direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backward = forward === 'ArrowRight' ? 'ArrowLeft' : 'ArrowRight';

    let next = current;

    switch (event.key) {
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      case forward:
        next = current === last ? 0 : current + 1;
        break;
      case backward:
        next = current === 0 ? last : current - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    this.#select(tabs[next]?.dataset.tab ?? null, { focus: true });
  };
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}
